import { useEffect, useRef, useState } from "react";

/**
 * Debounces a string value with support for re-triggering on same-value re-entry.
 * Returns [debouncedValue, triggerVersion] — version increments on every committed change,
 * even if the debounced value is the same as before.
 */
export const useDebounce = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Same as useDebounce but also returns a version counter that increments
 * every time the source value changes, even if debounced result is identical.
 * Use the version as a useEffect dependency to force re-fetch.
 */
export const useDebounceWithTrigger = (value: string, delay: number): [string, number] => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [version, setVersion] = useState(0);
  const committedRef = useRef(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
      // Bump version if this is a new "commit" cycle (value changed since last commit)
      if (committedRef.current !== value || value !== debouncedValue) {
        setVersion((v) => v + 1);
      }
      committedRef.current = value;
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return [debouncedValue, version];
};
